/**
 * Buhlmann.js
 *
 * Copyright (c) 2026 Eric Seigne <eric.seigne@cap-rel.fr>
 *
 * This program is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Affero General Public License as
 * published by the Free Software Foundation, either version 3 of the
 * License, or (at your option) any later version.
 *
 * This program is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
 * GNU Affero General Public License for more details.
 *
 * You should have received a copy of the GNU Affero General Public License
 * along with this program.  If not, see <https://www.gnu.org/licenses/>.
 */

import { debugBuhlmann, debugBuhlmannTissues, debugBuhlmannCeiling } from '../utils/debug.js'
import { Gas } from './Gas.js'
import { PlanError, PLAN_ERROR_CODES } from './errors.js'

// Buhlmann ZHL-16B/C decompression model
// 16 tissue compartments with N2 and He half-times

export class Buhlmann {
  // Algorithm variants
  static ALGORITHMS = {
    ZHL16B: 'zhl16b',
    ZHL16C: 'zhl16c'
  }

  // ZHL-16B coefficients (N2) - original Buhlmann (1b variant with 5min first compartment)
  // Source: Subsurface divelog, MVPlan Java, Bühlmann "Tauchmedizin"
  static ZHL16B_N2_HALFTIMES = [5.0, 8.0, 12.5, 18.5, 27.0, 38.3, 54.3, 77.0, 109.0, 146.0, 187.0, 239.0, 305.0, 390.0, 498.0, 635.0]
  static ZHL16B_N2_A = [1.1696, 1.0000, 0.8618, 0.7562, 0.6667, 0.5600, 0.4947, 0.4500, 0.4187, 0.3798, 0.3497, 0.3223, 0.2850, 0.2737, 0.2523, 0.2327]
  static ZHL16B_N2_B = [0.5578, 0.6514, 0.7222, 0.7825, 0.8126, 0.8434, 0.8693, 0.8910, 0.9092, 0.9222, 0.9319, 0.9403, 0.9477, 0.9544, 0.9602, 0.9653]

  // ZHL-16C coefficients (N2) - modified for dive computers (more conservative a values for compartments 5-15)
  // Source: Subsurface divelog, MVPlan Java, Bühlmann "Tauchmedizin"
  // https://en.wikipedia.org/wiki/B%C3%BChlmann_decompression_algorithm
  static ZHL16C_N2_HALFTIMES = [5.0, 8.0, 12.5, 18.5, 27.0, 38.3, 54.3, 77.0, 109.0, 146.0, 187.0, 239.0, 305.0, 390.0, 498.0, 635.0]
  static ZHL16C_N2_A = [1.1696, 1.0000, 0.8618, 0.7562, 0.6200, 0.5043, 0.4410, 0.4000, 0.3750, 0.3500, 0.3295, 0.3065, 0.2835, 0.2610, 0.2480, 0.2327]
  static ZHL16C_N2_B = [0.5578, 0.6514, 0.7222, 0.7825, 0.8126, 0.8434, 0.8693, 0.8910, 0.9092, 0.9222, 0.9319, 0.9403, 0.9477, 0.9544, 0.9602, 0.9653]

  // Helium coefficients (same for B and C) - using 1b variant with 1.88min first compartment
  // Source: Subsurface divelog (github.com/subsurface/subsurface), Bühlmann "Tauchmedizin"
  static HE_HALFTIMES = [1.88, 3.02, 4.72, 6.99, 10.21, 14.48, 20.53, 29.11, 41.20, 55.19, 70.69, 90.34, 115.29, 147.42, 188.24, 240.03]
  static HE_A = [1.6189, 1.3830, 1.1919, 1.0458, 0.9220, 0.8205, 0.7305, 0.6502, 0.5950, 0.5545, 0.5333, 0.5189, 0.5181, 0.5176, 0.5172, 0.5119]
  static HE_B = [0.4770, 0.5747, 0.6527, 0.7223, 0.7582, 0.7957, 0.8279, 0.8553, 0.8757, 0.8903, 0.8997, 0.9073, 0.9122, 0.9171, 0.9217, 0.9267]

  // ln 2, the constant of every half time to rate conversion (k = ln2 / T).
  // Used to be the 0.69315 of MVPlan Java, kept for bit parity with it; the
  // reference this engine is now compared with is Subsurface, which uses
  // log(2), and the tests/reference oracle reads its k from the same value.
  // The relative gap was 4e-6, below anything a stop minute resolves.
  static LOG2 = Math.LN2

  // Alveolar water vapor pressure in bar, subtracted from the inspired mix
  static WATER_VAPOR_PRESSURE = 0.0627

  // Air breathed during the surface interval, for the off-gassing computations
  // below. Only fN2 and fHe are read by loadTissues().
  static SURFACE_AIR = { name: 'Air', fO2: 0.21, fN2: Gas.AIR_N2_FRACTION, fHe: 0 }

  // Residual inert gas overpressure, in bar, under which the tissues count as
  // back to their surface equilibrium. Desaturation is asymptotic and never
  // reaches zero exactly, so "fully desaturated" needs a threshold. 0.05 bar
  // puts the result in the range dive computers display: measured on the full
  // plan of a sea level air dive at GF 30/85, about 8 h after 20 m / 40 min
  // (476 min) and about 19 h 30 after 40 m / 40 min (1166 min).
  static DESATURATION_THRESHOLD = 0.05

  // Longest surface interval the desaturation search explores, in minutes.
  // Beyond that the answer is reported as unknown rather than guessed.
  static MAX_SURFACE_INTERVAL = 48 * 60

  // Number of compartments of the model. A serialized state carries exactly
  // that many: a shorter list would leave compartments at surface equilibrium
  // and silently clear part of the residual load of a repetitive dive.
  static TISSUE_COUNT = 16

  // Highest inert gas tension, in bar, a deserialized compartment may carry.
  // 20 bar is the nitrogen tension of a diver saturated on air near 240 m,
  // well beyond anything this planner produces: above that the state is
  // corrupted or hand edited, not the output of a dive.
  static MAX_TISSUE_PRESSURE = 20

  // Format version of the serialized tissue state. Written by toJSON, checked
  // by fromJSON: a state written by a future version with another compartment
  // layout must be refused, not read as if it were this one.
  static STATE_VERSION = 1

  // env = { altitude: <metres>, waterType: <preset key or density in g/l> },
  // both optional; Gas.waterDensity() lists the accepted water types and
  // refuses the others. Defaults are sea level and salt water, i.e. a surface
  // pressure of 1.01325 bar and 0.101043 bar per metre (see Gas.js).
  constructor(algorithm = Buhlmann.ALGORITHMS.ZHL16C, env = {}) {
    this.algorithm = algorithm
    this.env = env || {}
    // Initialize 16 compartments at surface equilibrium (air).
    // At sea level: ppN2 = fN2 * (pSurface - pH2O)
    //                    = 0.79 * (1.01325 - 0.0627) = 0.7509345 bar
    this.compartments = Array(16).fill(null).map(() => ({
      pN2: this.initialPpN2(),
      pHe: 0
    }))
  }

  // Initial N2 tension of a diver at equilibrium with the surface pressure of
  // the dive site. Modelling assumption: the diver is acclimatized to the
  // altitude, so the tissues start saturated at the local surface pressure and
  // not at sea level pressure.
  initialPpN2() {
    const pSurface = Gas.surfacePressure(this.env.altitude)
    return Gas.AIR_N2_FRACTION * (pSurface - Buhlmann.WATER_VAPOR_PRESSURE)
  }

  // Change the dive environment without touching the current tissue state
  // (used when the same loaded model must be replayed in another environment)
  setEnvironment(env) {
    this.env = env || {}
    debugBuhlmann('setEnvironment: altitude=%om, waterType=%o (tissues kept)',
      this.env.altitude || 0, this.env.waterType ?? 'salt')
  }

  // Get N2 coefficients based on algorithm
  get N2_HALFTIMES() {
    return this.algorithm === Buhlmann.ALGORITHMS.ZHL16B
      ? Buhlmann.ZHL16B_N2_HALFTIMES
      : Buhlmann.ZHL16C_N2_HALFTIMES
  }

  get N2_A() {
    return this.algorithm === Buhlmann.ALGORITHMS.ZHL16B
      ? Buhlmann.ZHL16B_N2_A
      : Buhlmann.ZHL16C_N2_A
  }

  get N2_B() {
    return this.algorithm === Buhlmann.ALGORITHMS.ZHL16B
      ? Buhlmann.ZHL16B_N2_B
      : Buhlmann.ZHL16C_N2_B
  }

  // Set algorithm variant
  setAlgorithm(algorithm) {
    this.algorithm = algorithm
  }

  // Reset tissues to surface saturation
  reset() {
    // At sea level: ppN2 = 0.79 * (1.01325 - 0.0627) = 0.7509345 bar.
    // Above sea level initialPpN2() follows the local surface pressure.
    const initialPpN2 = this.initialPpN2()
    this.compartments.forEach(c => {
      c.pN2 = initialPpN2
      c.pHe = 0
    })
    debugBuhlmann('reset tissues to surface saturation')
  }

  // Calculate tissue loading after time at depth with given gas
  // Uses Schreiner equation for instantaneous gas loading (constant depth)
  loadTissues(depth, time, gas) {
    const pAmb = Gas.pressureAtDepth(depth, this.env) // Ambient pressure in bar
    const ppN2 = gas.fN2 * (pAmb - Buhlmann.WATER_VAPOR_PRESSURE) // Inspired N2 (minus water vapor)
    const ppHe = (gas.fHe || 0) * (pAmb - Buhlmann.WATER_VAPOR_PRESSURE)

    debugBuhlmann('loadTissues: depth=%om, time=%omin, gas=%s (fN2=%o, fHe=%o)', depth, time, gas.name, gas.fN2, gas.fHe || 0)
    debugBuhlmannTissues('  pAmb=%o bar, ppN2=%o, ppHe=%o', pAmb, ppN2, ppHe)

    for (let i = 0; i < 16; i++) {
      // N2 loading
      const kN2 = Buhlmann.LOG2 / this.N2_HALFTIMES[i]
      const prevN2 = this.compartments[i].pN2
      this.compartments[i].pN2 += (ppN2 - this.compartments[i].pN2) * (1 - Math.exp(-kN2 * time))

      // He loading
      const kHe = Buhlmann.LOG2 / Buhlmann.HE_HALFTIMES[i]
      const prevHe = this.compartments[i].pHe
      this.compartments[i].pHe += (ppHe - this.compartments[i].pHe) * (1 - Math.exp(-kHe * time))

      if (i === 0 || i === 4 || i === 8 || i === 15) {
        debugBuhlmannTissues('  C%d: pN2 %o->%o, pHe %o->%o (t½N2=%o, t½He=%o)',
          i + 1, prevN2.toFixed(3), this.compartments[i].pN2.toFixed(3), prevHe.toFixed(3), this.compartments[i].pHe.toFixed(3),
          this.N2_HALFTIMES[i], Buhlmann.HE_HALFTIMES[i])
      }
    }
  }

  // Calculate tissue loading during ascent/descent with changing pressure
  // Uses Schreiner equation for variable pressure: P = Pio + R(t - 1/k) - [Pio - Po - R/k] * e^(-kt)
  // This matches MVPlan Java's ascDec() method in Compartment.java
  loadTissuesAscDec(startDepth, endDepth, time, gas) {
    const pH2O = Buhlmann.WATER_VAPOR_PRESSURE // Water vapor pressure in bar
    const pAmbStart = Gas.pressureAtDepth(startDepth, this.env)
    const pAmbEnd = Gas.pressureAtDepth(endDepth, this.env)

    // Initial inspired partial pressures
    const ppN2Inspired = gas.fN2 * (pAmbStart - pH2O)
    const ppHeInspired = (gas.fHe || 0) * (pAmbStart - pH2O)

    // Rate of change of inspired partial pressure (bar/min). Written this way
    // and not as (end - start) / time: the two are not the same last bit, and
    // the open circuit reference scenarios are pinned to this one.
    const rateN2 = gas.fN2 * (pAmbEnd - pAmbStart) / time
    const rateHe = (gas.fHe || 0) * (pAmbEnd - pAmbStart) / time

    debugBuhlmann('loadTissuesAscDec: %om->%om, time=%omin, gas=%s', startDepth, endDepth, time, gas.name)
    debugBuhlmannTissues('  ppN2i=%o, rateN2=%o, ppHei=%o, rateHe=%o', ppN2Inspired, rateN2, ppHeInspired, rateHe)

    this.schreiner(ppN2Inspired, rateN2, ppHeInspired, rateHe, time)
  }

  // Tissue loading between two inspired inert gas pressures that change
  // linearly over `time`: start and end are { ppN2, ppHe } in bar, already
  // corrected for water vapour.
  //
  // This is what a rebreather transition needs. At constant setpoint the
  // inspired inert pressure is (pAmb - setpoint - pH2O) times the diluent
  // ratio, linear in depth, so the Schreiner equation applies to it directly.
  // CCR descents and ascents used to be loaded as a stay at the mean depth,
  // which under-loads the fast compartments on a descent (compartment 1 read
  // 1.41 bar instead of 1.48 after 0 -> 60 m on air at 1.3 bar) while the
  // open circuit transitions were integrated exactly.
  loadTissuesInspired(start, end, time) {
    const rateN2 = (end.ppN2 - start.ppN2) / time
    const rateHe = (end.ppHe - start.ppHe) / time
    debugBuhlmann('loadTissuesInspired: ppN2 %o->%o, ppHe %o->%o, time=%omin',
      start.ppN2.toFixed(3), end.ppN2.toFixed(3), start.ppHe.toFixed(3), end.ppHe.toFixed(3), time)
    this.schreiner(start.ppN2, rateN2, start.ppHe, rateHe, time)
  }

  // Schreiner equation for every compartment, from the inspired inert
  // pressures at the start of the leg and their rates of change (bar/min):
  //   P(t) = Pio + R*(t - 1/k) - (Pio - Po - R/k) * e^(-k*t)
  schreiner(ppN2Inspired, rateN2, ppHeInspired, rateHe, time) {
    for (let i = 0; i < 16; i++) {
      const kN2 = Buhlmann.LOG2 / this.N2_HALFTIMES[i]
      const kHe = Buhlmann.LOG2 / Buhlmann.HE_HALFTIMES[i]

      const prevN2 = this.compartments[i].pN2
      const prevHe = this.compartments[i].pHe

      this.compartments[i].pN2 = ppN2Inspired + rateN2 * (time - 1 / kN2) -
        (ppN2Inspired - prevN2 - rateN2 / kN2) * Math.exp(-kN2 * time)

      this.compartments[i].pHe = ppHeInspired + rateHe * (time - 1 / kHe) -
        (ppHeInspired - prevHe - rateHe / kHe) * Math.exp(-kHe * time)

      if (i === 0 || i === 4 || i === 8 || i === 15) {
        debugBuhlmannTissues('  C%d: pN2 %o->%o, pHe %o->%o',
          i + 1, prevN2.toFixed(3), this.compartments[i].pN2.toFixed(3),
          prevHe.toFixed(3), this.compartments[i].pHe.toFixed(3))
      }
    }
  }

  // Ambient pressure a compartment tolerates, in bar, with the gradient
  // factor interpolated between GF Low at `pRef` (the reference pressure,
  // see DivePlanner.gfReferenceFloor) and GF High at the surface `pSurface`.
  //
  // Self consistent: the gradient factor applied is the one of the tolerated
  // pressure itself, not of the depth the question is asked from. The
  // interpolation is linear in pressure, so the fixed point has a closed form;
  // it is Subsurface's (core/deco.cpp, tissue_tolerance_calc), transcribed
  // term for term. Evaluating the ceiling with the gradient factor of the
  // depth being LEFT FOR, as the engine used to, understated it whenever the
  // true ceiling was deeper than that depth: on a 100 m rebreather dive at
  // GF 35/75, 41.5 m instead of 43.1 m at the moment of leaving 45 m, i.e. a
  // stop skipped. When GF Low is not reachable (pRef at or above the surface)
  // GF High applies throughout.
  //
  // Returns 0 for a compartment that binds nowhere, which is how Subsurface's
  // guard on the two M-value lines reads: when the line at the surface under
  // GF High already tolerates more than the line at the reference under GF
  // Low, the interpolation runs the wrong way and the compartment sets no
  // ceiling of its own.
  static toleratedPressure(pTotal, a, b, gfLow, gfHigh, pSurface, pRef) {
    if (!(pRef > pSurface)) {
      const gf = gfHigh
      return (pTotal - a * gf) / (gf / b + 1 - gf)
    }
    const atSurface = (pSurface / b + a - pSurface) * gfHigh + pSurface
    const atReference = (pRef / b + a - pRef) * gfLow + pRef
    if (atSurface >= atReference) return 0
    return (-a * b * (gfHigh * pRef - gfLow * pSurface)
      - (1 - b) * (gfHigh - gfLow) * pRef * pSurface
      + b * (pRef - pSurface) * pTotal)
      / (-a * b * (gfHigh - gfLow)
        + (1 - b) * (gfLow * pRef - gfHigh * pSurface)
        + b * (pRef - pSurface))
  }

  // Weighted Buhlmann coefficients of a compartment loaded with both gases
  coefficients(i) {
    const pTotal = this.compartments[i].pN2 + this.compartments[i].pHe
    const fracN2 = this.compartments[i].pN2 / (pTotal || 1)
    const fracHe = this.compartments[i].pHe / (pTotal || 1)
    return {
      pTotal,
      a: fracN2 * this.N2_A[i] + fracHe * Buhlmann.HE_A[i],
      b: fracN2 * this.N2_B[i] + fracHe * Buhlmann.HE_B[i]
    }
  }

  // Ceiling (minimum safe depth) with gradient factors, in metres, never
  // negative.
  //
  // referenceDepth null: GF Low everywhere, the raw ceiling that seeds the
  // reference of the dive. Otherwise GF Low at referenceDepth, GF High at the
  // surface, applied self consistently (toleratedPressure): the ceiling no
  // longer depends on the depth it is asked from, and `currentDepth` only
  // serves the debug trace.
  ceiling(gfLow, gfHigh, currentDepth, referenceDepth = null) {
    let maxCeiling = 0
    let controllingCompartment = 0
    const pSurface = Gas.surfacePressure(this.env.altitude)
    const pRef = referenceDepth === null ? null : Gas.pressureAtDepth(referenceDepth, this.env)

    for (let i = 0; i < 16; i++) {
      const { pTotal, a, b } = this.coefficients(i)
      const pTol = referenceDepth === null
        ? (pTotal - a * gfLow) / (gfLow / b + 1 - gfLow)
        : Buhlmann.toleratedPressure(pTotal, a, b, gfLow, gfHigh, pSurface, pRef)

      const ceiling = Gas.depthAtPressure(pTol, this.env)
      if (ceiling > maxCeiling) {
        maxCeiling = ceiling
        controllingCompartment = i + 1
      }
    }

    const result = Math.max(0, maxCeiling)
    debugBuhlmannCeiling('ceiling: depth=%om, reference=%s, GF=%o/%o -> ceiling=%om (C%d)',
      currentDepth, referenceDepth !== null ? `${referenceDepth}m` : 'null', Math.round(gfLow * 100), Math.round(gfHigh * 100), result.toFixed(1), controllingCompartment)

    return result
  }

  // Gradient factor applied at a depth, given the reference of the dive: the
  // interpolation the self consistent ceiling is built on, for display.
  gradientFactorAt(depth, gfLow, gfHigh, referenceDepth) {
    if (referenceDepth === null || !(referenceDepth > 0)) return referenceDepth === null ? gfLow : gfHigh
    const gf = gfHigh + (gfLow - gfHigh) * (depth / referenceDepth)
    return Math.max(gfLow, Math.min(gfHigh, gf))
  }

  // Get leading tissue info for current state
  // Returns the compartment with the highest MV gradient (closest to M-value limit)
  // This matches MVPlan Java's controlCompartment() and mValue() methods
  getLeadingTissueInfo(gfLow, gfHigh, currentDepth, referenceDepth = null) {
    let maxMvGradient = -Infinity
    let leadingInfo = null
    const pAmb = Gas.pressureAtDepth(currentDepth, this.env)
    const pSurface = Gas.surfacePressure(this.env.altitude)
    const pRef = referenceDepth === null ? null : Gas.pressureAtDepth(referenceDepth, this.env)

    for (let i = 0; i < 16; i++) {
      const pN2 = this.compartments[i].pN2
      const pHe = this.compartments[i].pHe
      const { pTotal, a, b } = this.coefficients(i)

      // M-value at current depth (max tolerated inert gas pressure)
      const mValue = a + pAmb / b

      // MV gradient = pTotal / (pAmb/b + a) = ratio of tissue pressure to M-value
      // This is how Java calculates getMV()
      const mvGradient = pTotal / (pAmb / b + a)

      if (mvGradient > maxMvGradient) {
        maxMvGradient = mvGradient

        // Same ceiling as ceiling(): self consistent under the reference
        const pTol = referenceDepth === null
          ? (pTotal - a * gfLow) / (gfLow / b + 1 - gfLow)
          : Buhlmann.toleratedPressure(pTotal, a, b, gfLow, gfHigh, pSurface, pRef)
        const ceiling = Gas.depthAtPressure(pTol, this.env)
        // Gradient factor in force at that ceiling, the one the M-value line
        // was drawn with
        const gf = this.gradientFactorAt(Math.max(0, ceiling), gfLow, gfHigh, referenceDepth)

        // Share of the M-value the leading compartment has reached at this
        // depth, in percent (100 % = at the M-value line). The field is named
        // gfPercent for historical reasons and the reference scenarios read it
        // under that name: it is NOT the gradient factor applied, which is
        // gfUsed just below.
        const gfPercent = Math.round(mvGradient * 100)

        leadingInfo = {
          compartment: i + 1, // 1-indexed for display
          ceiling: Math.max(0, ceiling),
          pTotal: pTotal,
          mValue: mValue,
          gfUsed: Math.round(gf * 100),
          gfPercent: Math.max(0, gfPercent),
          leadingGas: pN2 > pHe ? 'N2' : (pHe > 0 ? 'He' : 'N2'),
          pN2: pN2,
          pHe: pHe,
          halfTimeN2: this.N2_HALFTIMES[i],
          halfTimeHe: Buhlmann.HE_HALFTIMES[i]
        }
      }
    }

    return leadingInfo
  }

  // Get full compartment state for visualization
  getCompartmentState(depth) {
    const pAmb = Gas.pressureAtDepth(depth, this.env)
    return this.compartments.map((c, i) => {
      const pTotal = c.pN2 + c.pHe
      const fracN2 = c.pN2 / (pTotal || 1)
      const fracHe = c.pHe / (pTotal || 1)
      const a = fracN2 * this.N2_A[i] + fracHe * Buhlmann.HE_A[i]
      const b = fracN2 * this.N2_B[i] + fracHe * Buhlmann.HE_B[i]
      const mValue = a + pAmb / b
      const saturation = pAmb >= mValue ? 100 : Math.round((pTotal / mValue) * 100)

      return {
        compartment: i + 1,
        pN2: c.pN2,
        pHe: c.pHe,
        pTotal: pTotal,
        mValue: mValue,
        a: a,
        b: b,
        saturation: Math.min(100, saturation),
        halfTimeN2: this.N2_HALFTIMES[i],
        halfTimeHe: Buhlmann.HE_HALFTIMES[i]
      }
    })
  }

  // Inert gas overpressure of the most loaded compartment, in bar, measured
  // against the surface equilibrium of the dive site. Zero once every
  // compartment is back at (or below) that equilibrium.
  residualOverpressure() {
    const equilibrium = this.initialPpN2()
    return this.compartments.reduce(
      (max, c) => Math.max(max, c.pN2 + c.pHe - equilibrium),
      0
    )
  }

  // Minutes at the surface of the dive site, breathing air, before every
  // compartment is back to its surface equilibrium within
  // DESATURATION_THRESHOLD.
  //
  // Found by bisection: off-gassing never reverses, so the criterion goes from
  // false to true exactly once. Each probe restarts from the current state and
  // loads it in a single step, which is exact at constant pressure - stepping
  // minute by minute would only add rounding.
  //
  // Returns null when the tissues are still loaded after MAX_SURFACE_INTERVAL,
  // and logs it: no plausible dive gets there, so reaching that point means the
  // caller fed a state the model never produces.
  desaturationTime() {
    const isDesaturated = model =>
      model.residualOverpressure() <= Buhlmann.DESATURATION_THRESHOLD

    if (isDesaturated(this)) return 0

    const afterSurfaceTime = minutes => {
      const model = this.clone()
      model.loadTissues(0, minutes, Buhlmann.SURFACE_AIR)
      return model
    }

    if (!isDesaturated(afterSurfaceTime(Buhlmann.MAX_SURFACE_INTERVAL))) {
      console.error('desaturationTime: tissues still loaded after %o min at the surface, no value returned',
        Buhlmann.MAX_SURFACE_INTERVAL)
      return null
    }

    let low = 0
    let high = Buhlmann.MAX_SURFACE_INTERVAL
    while (high - low > 1) {
      const mid = Math.floor((low + high) / 2)
      if (isDesaturated(afterSurfaceTime(mid))) high = mid
      else low = mid
    }

    debugBuhlmann('desaturationTime: %o min at the surface', high)
    return high
  }

  // Off-gas at the surface of the dive site, breathing air, for `minutes`.
  //
  // That is the whole physics of a surface interval: the tissues sit at the
  // local surface pressure with an air mix. A single loading step is exact at
  // constant pressure (loadTissues solves the exponential in closed form), so
  // stepping minute by minute would only add rounding.
  applySurfaceInterval(minutes, context = 'Buhlmann.applySurfaceInterval') {
    if (!Number.isFinite(minutes) || minutes < 0) {
      console.error('%s: invalid surface interval (%o), expected a finite duration >= 0 min', context, minutes)
      throw new PlanError(PLAN_ERROR_CODES.INVALID_SURFACE_INTERVAL, { context, minutes })
    }

    if (minutes === 0) {
      debugBuhlmann('applySurfaceInterval: 0 min, tissues left as they are')
      return
    }

    this.loadTissues(0, minutes, Buhlmann.SURFACE_AIR)
    debugBuhlmann('applySurfaceInterval: %o min at the surface, residual overpressure %o bar',
      minutes, this.residualOverpressure().toFixed(3))
  }

  // Serialize the tissue state so it can be persisted (localStorage, saved
  // plan, exported file) and replayed as the entry state of a repetitive dive.
  //
  // Only the compartment tensions are contractual. algorithm and altitude are
  // written for diagnosis: tensions are pressures and do not depend on the a/b
  // coefficients, so a state produced under ZHL-16B stays physically
  // meaningful when a ZHL-16C plan starts from it.
  toJSON() {
    return {
      version: Buhlmann.STATE_VERSION,
      algorithm: this.algorithm,
      altitude: this.env.altitude || 0,
      compartments: this.compartments.map(c => ({ pN2: c.pN2, pHe: c.pHe }))
    }
  }

  // Rebuild a model from a serialized state, under the given algorithm and
  // environment (those of the dive being planned, not those the state was
  // written with).
  //
  // Validation is strict and every rejection is logged before it throws: a
  // truncated or corrupted state read as valid would leave compartments at
  // surface equilibrium, which reads as "these tissues are clean" and produces
  // a repetitive dive plan with less decompression than the diver owes.
  static fromJSON(state, algorithm = Buhlmann.ALGORITHMS.ZHL16C, env = {}, context = 'Buhlmann.fromJSON') {
    const refuse = (reason, details = {}) => {
      console.error('%s: unusable tissue state (%s) %o', context, reason, details)
      throw new PlanError(PLAN_ERROR_CODES.INVALID_TISSUE_STATE, { context, reason, ...details })
    }

    if (!state || typeof state !== 'object' || Array.isArray(state)) {
      refuse('not_an_object', { state })
    }
    if (state.version !== Buhlmann.STATE_VERSION) {
      refuse('unknown_version', { version: state.version, expected: Buhlmann.STATE_VERSION })
    }
    if (!Array.isArray(state.compartments) || state.compartments.length !== Buhlmann.TISSUE_COUNT) {
      refuse('wrong_compartment_count', {
        count: Array.isArray(state.compartments) ? state.compartments.length : null,
        expected: Buhlmann.TISSUE_COUNT
      })
    }

    const model = new Buhlmann(algorithm, env)

    // Indexed loop and not forEach: forEach skips the holes of a sparse array,
    // so `new Array(16)` would pass the length check, skip every per
    // compartment check, and leave the model at surface equilibrium - a
    // repetitive dive whose residual load was silently dropped, which is the
    // one failure this whole function exists to prevent.
    for (let index = 0; index < Buhlmann.TISSUE_COUNT; index++) {
      const compartment = state.compartments[index]
      if (!compartment || typeof compartment !== 'object') {
        refuse('compartment_not_an_object', { index, compartment })
      }
      for (const gas of ['pN2', 'pHe']) {
        const value = compartment[gas]
        if (!Number.isFinite(value) || value < 0 || value > Buhlmann.MAX_TISSUE_PRESSURE) {
          refuse('tension_out_of_range', {
            index, gas, value, min: 0, max: Buhlmann.MAX_TISSUE_PRESSURE
          })
        }
      }
      model.compartments[index] = { pN2: compartment.pN2, pHe: compartment.pHe }
    }

    if (state.algorithm && state.algorithm !== algorithm) {
      debugBuhlmann('fromJSON: state written with %s, replayed under %s (tensions are algorithm independent)',
        state.algorithm, algorithm)
    }
    debugBuhlmann('fromJSON: tissue state restored, residual overpressure %o bar',
      model.residualOverpressure().toFixed(3))

    return model
  }

  // Clone current state (environment included)
  clone() {
    const copy = new Buhlmann(this.algorithm, { ...this.env })
    copy.compartments = this.compartments.map(c => ({ ...c }))
    return copy
  }
}
